import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';

// ---------------------------------------------------------------------------
// Game UI Configuration System
// ---------------------------------------------------------------------------
// Centralized, extensible config map for per-game UI customizations.
// To add a new game just insert an entry into `gameUIConfigs` – no logic
// changes required anywhere else.
// ---------------------------------------------------------------------------

/** Supported rendering types for configurable UI elements. */
export type UIRenderType = 'image' | 'default';

/** Per-element config shape */
export interface ElementUIConfig {
  /** How the element should be rendered. */
  type: UIRenderType;
}

// Add this interface
export interface SpineAnimationConfig {
  path: string;
  atlas: string;
  skeleton: string;
  // Add other properties your Spine animator expects
  runAnimation?: string;
  fallAnimation?: string;
}

export interface GameUIConfig {
  betButton: ElementUIConfig;
  quickBet: ElementUIConfig;
  betPanel?: ElementUIConfig;
  // Add this line to fix the "Property does not exist" error
  spineAnimations?: SpineAnimationConfig;
}

// ---------------------------------------------------------------------------
// Config Map  –  add new games here
// ---------------------------------------------------------------------------

const gameUIConfigs: Record<string, GameUIConfig> = {
  skysniffer: {
    betButton: { type: 'image' },
    quickBet: { type: 'image' },
    betPanel: { type: 'image' },
  },
  'sky-sniffer': {
    betButton: { type: 'image' },
    quickBet: { type: 'image' },
    betPanel: { type: 'image' },
  },
  skysnifferv2: {
    betButton: { type: 'image' },
    quickBet: { type: 'image' },
    betPanel: { type: 'image' },
  },
  'sky-sniffer-v2': {
    betButton: { type: 'image' },
    quickBet: { type: 'image' },
    betPanel: { type: 'image' },
  },
  citysniffer: {
    betButton: { type: 'image' },
    quickBet: { type: 'image' },
    betPanel: { type: 'image' },
  },
  citysnifferv2: {
    betButton: { type: 'image' },
    quickBet: { type: 'image' },
    betPanel: { type: 'image' },
  },
  // Future games – just add entries:
  // anothergame: {
  //   betButton: { type: 'image' },
  //   quickBet:  { type: 'default' },
  // },
};

// ---------------------------------------------------------------------------
// Config Resolver
// ---------------------------------------------------------------------------

/**
 * Returns the UI config for the given game, or `null` when no custom config
 * exists (→ the caller should fall back to the default UI).
 */
export function getUIConfig(
  gameName: string | undefined | null
): GameUIConfig | null {
  if (!gameName) {
    console.log('[UI CONFIG] No gameName provided');
    return null;
  }

  const normalized = gameName.toLowerCase();
  const config = gameUIConfigs[normalized] ?? null;

  if (config) {
    console.log(`[UI CONFIG] Found config for ${normalized}`);
  } else {
    console.log(`[UI CONFIG] No config for ${normalized}, using default UI`);
  }

  return config;
}

// ---------------------------------------------------------------------------
// Asset Path Helper
// ---------------------------------------------------------------------------

/**
 * Builds a public-folder path to a game-specific UI asset.
 *
 * Convention: `/games/<game>/images/UIElements/<fileName>`
 */
export function getGameAssetPath(
  gameName: string | undefined | null,
  fileName: string
): string {
  const folder = resolveAssetFolderSlug(gameName);
  return buildGamePublicAssetUrl(folder, `images/UIElements/${fileName}`);
}
